import QRCode from 'qrcode';

/**
 * Prints a badge for a hacker using a Brother QL-810W label printer.
 * Opens a new window with the badge sized to 62mm × 40mm and triggers the print dialog.
 *
 * @param {string} firstName - Hacker's first name (displayed large)
 * @param {string} lastName - Hacker's last name (displayed smaller)
 * @param {string|null} applicationId - The MongoDB ObjectID used in the QR code URL (used if qrUrl is not provided)
 * @param {string} [qrUrl] - Optional direct URL for the QR code. If omitted, built from applicationId.
 */
export async function printBadge(firstName, lastName, applicationId, qrUrl) {
  const resolvedQrUrl =
    qrUrl || (applicationId ? `https://trunk.treehacks.com/p/${applicationId}` : null);

  let qrDataUrl = null;
  if (resolvedQrUrl) {
    try {
      qrDataUrl = await QRCode.toDataURL(resolvedQrUrl, {
        width: 200,
        margin: 0,
        errorCorrectionLevel: 'M',
      });
    } catch (err) {
      console.error('Failed to generate QR code:', err);
    }
  }

  const badgeHTML = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Badge - ${firstName} ${lastName}</title>
  <style>
    @page {
      size: 60mm 40mm;
      margin: 0;
    }

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    html, body {
      width: 62mm;
      height: 29mm;
      margin: 0;
      padding: 0;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      overflow: hidden;
    }

    .badge {
      width: 62mm;
      height: 29mm;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }

    .name-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      text-align: center;
      margin-top: -1mm;
    }

    .first-name {
      font-weight: 600;
      line-height: 11mm;
      font-size: 10mm;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: center;
      width: 100%;
    }

    .last-name {
      font-weight: 600;
      line-height: 5mm;
      font-size: 6mm;
      margin-top: -1mm;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: center;
      width: 100%;
    }

    .qr-section {
      margin-top: 1mm;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .qr-section img {
      width: 11mm;
      height: 11mm;
    }

    @media screen {
      body {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: #f0f0f0;
      }
      .badge {
        border: 1px dashed #ccc;
        background: white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      }
    }
  </style>
</head>
<body>
  <div class="badge">
    <div class="name-section">
      <div class="first-name">${escapeHtml(firstName)}</div>
      <div class="last-name">${escapeHtml(lastName)}</div>
    </div>
    ${qrDataUrl ? `<div class="qr-section"><img src="${qrDataUrl}" alt="QR Code" /></div>` : ''}
  </div>
  <script>
    window.onafterprint = function() {
      window.close();
    };
    // Small delay to ensure rendering is complete
    setTimeout(function() {
      window.print();
    }, 250);
  </script>
</body>
</html>`;

  const printWindow = window.open('', '_blank', 'width=600,height=400');
  if (!printWindow) {
    alert('Please allow popups to print badges.');
    return;
  }
  printWindow.document.write(badgeHTML);
  printWindow.document.close();
}

/**
 * Splits a full name into first and last name.
 * @param {string} fullName
 * @returns {{ firstName: string, lastName: string }}
 */
export function splitName(fullName) {
  if (!fullName) return { firstName: '', lastName: '' };
  const parts = fullName.trim().split(/\s+/);
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ') || '';
  return { firstName, lastName };
}

function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
